// pages/organization/OrgCategoriesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import { Helmet } from "react-helmet";
import { httpRequest } from "../../utils/httpRequest";
import CategoryCard from "../../components/organization/CategoryCard";
import { useTranslation } from "react-i18next";
import { Plus, ChevronLeft, ChevronRight, Home, Shield } from "lucide-react";
import CreateCategoryModal from "../../components/organization/CreateCategoryModal";
import EditCategoryModal from "../../components/organization/EditCategoryModal";
import { toast } from "react-toastify";
import { canWrite } from "../../components/auth/perm";
import {
  computeAllowedCategorySet,
  buildCategoryTree,
} from "../../utils/categories";
import { useSession } from "../../session/SessionProvider";
import ConfirmModal from "../../components/common/ConfirmModal";

const OrgCategoriesPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");

  const [loading, setLoading] = useState(true);
  const [roots, setRoots] = useState([]);
  const [current, setCurrent] = useState([]);
  const [path, setPath] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCategoryId, setConfirmCategoryId] = useState(null);
  const { user, refreshUser } = useSession();
  const mayCreate = canWrite(user, "categories");

  // اسکوپ دسته‌ها از پروفایل کاربر
  const categoryScopes = Array.isArray(user?.categoryScopes)
    ? user.categoryScopes
    : [];

  const getId = (n) => n?._id ?? n?.id ?? null;
  const getParentId = (n) => {
    const p = n?.parentId ?? n?.parent ?? null;
    if (!p) return null;
    if (typeof p === "string") return p;
    if (typeof p === "object") return p._id ?? p.id ?? null;
    return null;
  };
  const getLevel = (n) => {
    const num = Number(n?.level);
    return Number.isFinite(num) ? num : 0;
  };

  /** مسیر اجداد (level تا بالا) را برمی‌گرداند */
  function collectAncestors(byId, startId, guardMax = 50) {
    const out = [];
    let curId = String(startId);
    let guard = 0;
    while (guard++ < guardMax) {
      const node = byId.get(curId);
      if (!node) break;
      const pid = getParentId(node);
      if (!pid) break;
      out.push(String(pid));
      curId = String(pid);
    }
    return out;
  }

  /** pathSet = scoped + ancestors؛ rootsL1 = level1 ∩ pathSet */
  function computeScopedPathSets(allCats, scopedIds) {
    const byId = new Map(allCats.map((c) => [String(getId(c)), c]));
    const pathSet = new Set();
    const scoped = new Set((scopedIds || []).map(String));

    for (const id of scoped) {
      pathSet.add(id);
      for (const anc of collectAncestors(byId, id)) pathSet.add(anc);
    }
    const rootsL1 = allCats.filter(
      (c) => getLevel(c) === 1 && pathSet.has(String(getId(c)))
    );
    return { pathSet, rootsL1, byId };
  }

  // API helpers
  const listAll = async () =>
    httpRequest("GET", "/category", {}, { "x-access-token": user.token });

  const getOneWithChildren = async (id) =>
    httpRequest("GET", "/category/" + id, {}, { "x-access-token": user.token });

  // مجموعهٔ مجاز از روی اسکوپ‌ها + کل دسته‌ها
  const allowedSet = useMemo(() => {
    return computeAllowedCategorySet(allCategories, categoryScopes);
  }, [allCategories, categoryScopes]);

  // آیا فیلتر اسکوپ فعال است؟
  const scopeActive = useMemo(
    () => categoryScopes?.length > 0,
    [categoryScopes]
  );

  // بارگذاری اولیه
  useEffect(() => {
    const boot = async () => {
      setLoading(true);
      try {
        const res = await listAll();
        const all = res?.data?.data || [];
        setAllCategories(all);

        const scopedIds = Array.isArray(categoryScopes) ? categoryScopes : [];
        if (scopedIds.length > 0) {
          const { pathSet, rootsL1 } = computeScopedPathSets(all, scopedIds);
          // اگر هیچ level1 روی مسیر نبود، fallback: level1 های معمولی
          if (rootsL1.length > 0) {
            setRoots(rootsL1);
            setCurrent(rootsL1); // ✅ از «زبان» شروع می‌کنیم، نه «تافل»
            setPath([]);
          } else {
            const level1 = all.filter((c) => getLevel(c) === 1);
            setRoots(level1);
            setCurrent(level1);
            setPath([]);
          }
        } else {
          const level1 = all.filter((c) => getLevel(c) === 1);
          setRoots(level1);
          setCurrent(level1);
          setPath([]);
        }
      } catch (e) {
        setRoots([]);
        setCurrent([]);
        setPath([]);
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, []);

  // ورود به دسته (فقط اگر در اسکوپ باشد)
  const handleEnter = async (cat) => {
    const id = String(cat._id);

    // اگر داخل مسیر اسکوپ نیست، اجازهٔ ورود نده
    const { pathSet } = computeScopedPathSets(allCategories, categoryScopes);
    if (categoryScopes?.length && !pathSet.has(id)) {
      toast.error(t("شما به این دسته دسترسی ندارید."));
      return;
    }

    setLoading(true);
    try {
      const res = await getOneWithChildren(id);
      const { category, children } = res?.data?.data || {};
      const nextPath = [
        ...path,
        { _id: category._id, title: category.title, level: category.level },
      ];
      setPath(nextPath);

      // فرزندان روی «مسیر اسکوپ»
      const { pathSet } = computeScopedPathSets(allCategories, categoryScopes);
      const filteredChildren = (children || []).filter((c) =>
        pathSet.has(String(c._id))
      );
      setCurrent(filteredChildren);
    } catch (e) {
      toast.error(t("common.unknown_error"));
    } finally {
      setLoading(false);
    }
  };

  // برگشت با breadcrumb
  const handleCrumbClick = async (index) => {
    if (index === -1) {
      setPath([]);
      // برگشت به ریشه‌های مسیر (نه برگِ scoped)
      const { rootsL1 } = computeScopedPathSets(allCategories, categoryScopes);
      setCurrent(rootsL1.length ? rootsL1 : roots);
      return;
    }
    const target = path[index];
    setLoading(true);
    try {
      const res = await getOneWithChildren(target._id);
      const { children } = res?.data?.data || {};

      const { pathSet } = computeScopedPathSets(allCategories, categoryScopes);
      const filteredChildren = (children || []).filter((c) =>
        pathSet.has(String(c._id))
      );
      setPath(path.slice(0, index + 1));
      setCurrent(filteredChildren);
    } catch {
      toast.error(t("common.unknown_error"));
    } finally {
      setLoading(false);
    }
  };

  // تازه‌سازی درخت (پس از CRUD)
  const refreshTree = async () => {
    setLoading(true);
    try {
      const res = await listAll();
      const all = res?.data?.data || [];
      setAllCategories(all);

      const { rootsL1, pathSet } = computeScopedPathSets(all, categoryScopes);
      const level1 = all.filter((c) => getLevel(c) === 1);
      setRoots(rootsL1.length ? rootsL1 : level1);

      if (path.length === 0) {
        setCurrent(rootsL1.length ? rootsL1 : level1);
      } else {
        const last = path[path.length - 1];
        const r2 = await getOneWithChildren(last._id);
        const { children } = r2?.data?.data || {};
        setCurrent(
          categoryScopes?.length
            ? (children || []).filter((c) => pathSet.has(String(c._id)))
            : children || []
        );
      }
    } catch {
      setPath([]);
      setCurrent([]);
    } finally {
      refreshUser();
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setIsEditOpen(true);
  };

  const handleDelete = async (categoryId) => {
    try {
      await httpRequest(
        "DELETE",
        "/category/" + categoryId,
        {},
        { "x-access-token": user.token }
      );
      await refreshTree();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  const askDeleteCategory = (categoryId) => {
    setConfirmCategoryId(categoryId);
    setConfirmOpen(true);
  };

  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  } space-y-4`;

  const Breadcrumb = () => (
    <div
      className={`flex items-center flex-wrap gap-2 text-sm ${
        isRtl ? "justify-start" : "justify-start"
      }`}
    >
      <button
        onClick={() => handleCrumbClick(-1)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border hover:bg-gray-50"
        title={t("all")}
      >
        <Home size={16} />
        <span>{t("all")}</span>
      </button>

      {path.map((node, idx) => (
        <React.Fragment key={node._id}>
          {isRtl ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          <button
            onClick={() => handleCrumbClick(idx)}
            className="px-2 py-1 rounded-md bg-white border hover:bg-gray-50"
            title={node.title}
          >
            {node.title}
          </button>
        </React.Fragment>
      ))}

      {scopeActive && (
        <span className="ms-2 inline-flex items-center gap-1 text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded">
          <Shield size={14} />
          {t("نمایش بر اساس دسترسی")}
        </span>
      )}
    </div>
  );

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("org_categories.page_title")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />
        <main className={mainSectionClass}>
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800 ">
              {t("org_categories.page_header")}
            </h1>

            {/* ساخت فقط زمانی که حق نوشتن داری (قانون قبلی) */}
            {mayCreate && (
              <button
                className="bg-purple-600 rounded-lg px-5 py-2 text-white hover:bg-purple-700 duration-200 flex items-center gap-2"
                onClick={() => setIsCreateOpen(true)}
              >
                <Plus /> {t("org_categories.create_category")}
              </button>
            )}
          </div>

          <Breadcrumb />

          {loading ? (
            <p className="text-gray-500 text-sm">
              {t("org_categories.loading_message")}
            </p>
          ) : current.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {current.map((cat) => (
                <CategoryCard
                  key={cat._id}
                  category={cat}
                  onEdit={mayCreate ? (c) => handleEdit(c) : undefined}
                  onDelete={
                    mayCreate ? (id) => askDeleteCategory(id) : undefined
                  }
                  canEdit={mayCreate}
                  allowedCategoryIds={[...allowedSet]} // ← این خط جدید
                  showDisabledActions={false} // یا true اگر بخوای دکمه‌های غیرفعال دیده بشن
                  onClick={() => {
                    if (cat.hasChildren) handleEnter(cat);
                  }}
                />
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">
              {t("org_categories.empty_state_message")}
            </p>
          )}
        </main>
      </div>

      <CreateCategoryModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        token={user.token}
        defaultOrganizationId={user?.organization?._id}
        allCategories={allCategories}
        onCreated={() => refreshTree()}
      />

      <EditCategoryModal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setEditingCategory(null);
        }}
        token={user.token}
        category={editingCategory}
        allCategories={allCategories}
        onUpdated={() => refreshTree()}
      />

      <ConfirmModal
        open={confirmOpen}
        title={t("org_categories.delete_confirm_title", "حذف دسته‌بندی")}
        message={t(
          "org_categories.delete_confirm_message",
          "آیا از حذف این دسته‌بندی مطمئن هستید؟ در صورت داشتن زیرمجموعه یا وابستگی ممکن است حذف انجام نشود."
        )}
        confirmText={t("common.delete", "حذف")}
        cancelText={t("common.cancel", "انصراف")}
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmCategoryId(null);
        }}
        onConfirm={async () => {
          if (confirmCategoryId) {
            await handleDelete(confirmCategoryId);
          }
          setConfirmOpen(false);
          setConfirmCategoryId(null);
        }}
      />
    </div>
  );
};

export default OrgCategoriesPage;
